import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Plus, X, AlertTriangle, Tag } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import AdvertiserEditor from './AdvertiserEditor'
import BulkTagsModal from '../shared/BulkTagsModal'
import '../SponsorManager.css'

function advertiserField(a, ...keys) {
  for (const k of keys) {
    if (a?.[k] != null) return a[k]
  }
  return ''
}

// AS.Advertisers.getList returns only { id, name, status } per advertiser
// (where `name` IS the username — same field AdSpeed uses for the
// token recipe). The sponsor worker enriches each row with
// cached_fullname + cached_company drawn from a local D1 cache
// populated when admins visit per-advertiser detail pages. Empty
// cells fill in as detail pages are viewed.
function advertiserUsername(a) {
  return advertiserField(a, 'username', 'Username', 'name', 'Name')
}
function advertiserFullname(a) {
  return advertiserField(a, 'cached_fullname', 'fullname', 'Fullname', 'FullName')
}
function advertiserCompany(a) {
  return advertiserField(a, 'cached_company', 'company', 'Company')
}

// AdSpeed docs: advertiser status is one of active|pending|archived. No
// 'deleted' filter on Advertisers.getList.
const STATUS_TABS = [
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'archived', label: 'Archived' },
]

export default function AdvertisersList() {
  const { api } = useAuth()
  const navigate = useNavigate()
  const [advertisers, setAdvertisers] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stale, setStale] = useState(false)
  const [search, setSearch] = useState('')
  const [statusTab, setStatusTab] = useState('active')
  const [status, setStatus] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [bulkRunning, setBulkRunning] = useState(false)
  const [showBulkTags, setShowBulkTags] = useState(false)
  const [createdPassword, setCreatedPassword] = useState(null) // reveal-once modal

  const [tagFilter, setTagFilter] = useState('')
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [tagFilteredIds, setTagFilteredIds] = useState(null)
  const [sortBy, setSortBy] = useState('id')
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const loadAdvertisers = useCallback(async () => {
    try {
      const data = await api(`/sponsors/advertisers?status=${statusTab}`)
      setAdvertisers(data.advertisers || [])
      setTotal(data.total || (data.advertisers?.length ?? 0))
      setStale(!!data.stale)
      if (data.ok === false) {
        setStatus(`Error: ${data.error?.message || 'AdSpeed rejected the filter'}`)
      } else {
        setStatus('')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, statusTab])

  useEffect(() => {
    setLoading(true)
    loadAdvertisers().finally(() => setLoading(false))
    setSelected(new Set())
  }, [loadAdvertisers])

  useEffect(() => {
    api('/tags').then(d => setAllTbtvTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (!tagFilter) {
      setTagFilteredIds(null)
      return
    }
    api(`/sponsors/advertisers/by-tag?tag=${encodeURIComponent(tagFilter)}`)
      .then(d => {
        const ids = new Set((d.advertisers || []).map(a => String(a.id ?? a.AdvertiserId)))
        setTagFilteredIds(ids)
      })
      .catch(e => setStatus(`Tag filter error: ${e.message}`))
  }, [api, tagFilter])

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await api('/sponsors/cache/bust?prefix=advertisers:', { method: 'POST' })
      await loadAdvertisers()
      setStatus('Refreshed from AdSpeed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setRefreshing(false)
  }

  async function handleCreate(body) {
    const res = await api('/sponsors/advertisers', { method: 'POST', body: JSON.stringify(body) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the request')
    }
    setShowCreate(false)
    // If admin used auto-gen password, surface it in reveal-once modal.
    if (body.__revealPassword) {
      setCreatedPassword({ username: body.username, password: body.__revealPassword })
    } else {
      setStatus('Advertiser created')
    }
    await loadAdvertisers()
  }

  async function handleBulkTags(tags, mode) {
    const ids = Array.from(selected)
    setBulkRunning(true)
    let ok = 0, fail = 0
    for (const id of ids) {
      try {
        let finalTags = tags
        if (mode === 'add') {
          const existing = await api(`/sponsors/advertisers/${id}/tbtv-tags`)
          const existingNames = (existing?.tags || []).map(t => t.name)
          finalTags = Array.from(new Set([...existingNames, ...tags]))
        }
        await api(`/sponsors/advertisers/${id}/tbtv-tags`, {
          method: 'PUT',
          body: JSON.stringify({ tags: finalTags }),
        })
        ok++
      } catch {
        fail++
      }
    }
    setBulkRunning(false)
    setShowBulkTags(false)
    setSelected(new Set())
    setStatus(fail === 0 ? `Tagged ${ok} advertiser(s).` : `Tagged ${ok}, ${fail} failed.`)
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return advertisers.filter(a => {
      const id = String(advertiserField(a, 'id', 'AdvertiserId') || '')
      const username = String(advertiserUsername(a) || '').toLowerCase()
      const fullname = String(advertiserFullname(a) || '').toLowerCase()
      const company = String(advertiserCompany(a) || '').toLowerCase()
      if (q && !username.includes(q) && !fullname.includes(q) && !company.includes(q) && !id.toLowerCase().includes(q)) return false
      if (tagFilteredIds && !tagFilteredIds.has(id)) return false
      return true
    })
  }, [advertisers, search, tagFilteredIds])

  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let av, bv
      if (sortBy === 'id') {
        av = Number(advertiserField(a, 'id', 'AdvertiserId') || 0)
        bv = Number(advertiserField(b, 'id', 'AdvertiserId') || 0)
      } else if (sortBy === 'username') {
        av = String(advertiserUsername(a) || '').toLowerCase()
        bv = String(advertiserUsername(b) || '').toLowerCase()
      } else if (sortBy === 'fullname') {
        av = String(advertiserFullname(a) || '').toLowerCase()
        bv = String(advertiserFullname(b) || '').toLowerCase()
      } else if (sortBy === 'company') {
        av = String(advertiserCompany(a) || '').toLowerCase()
        bv = String(advertiserCompany(b) || '').toLowerCase()
      } else {
        const k = sortBy
        const K = k.charAt(0).toUpperCase() + k.slice(1)
        av = String(advertiserField(a, k, K) || '').toLowerCase()
        bv = String(advertiserField(b, k, K) || '').toLowerCase()
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sortBy, sortDir])

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleSelectAll() {
    const allIds = sorted.map(a => String(advertiserField(a, 'id', 'AdvertiserId')))
    if (allIds.every(id => selected.has(id))) setSelected(new Set())
    else setSelected(new Set(allIds))
  }

  const allVisibleSelected = sorted.length > 0
    && sorted.every(a => selected.has(String(advertiserField(a, 'id', 'AdvertiserId'))))

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Advertisers</h1>
          <span className="sponsors-count">{filtered.length} of {total}</span>
          {stale && (
            <span className="sponsors-stale-badge">
              <AlertTriangle size={11} /> Stale cache
            </span>
          )}
        </div>
        <div className="sponsors-header-right">
          <button
            className="sponsors-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New advertiser
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="adtag-format-row" style={{ marginBottom: 10 }}>
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            className={`adtag-format-btn ${statusTab === t.value ? 'active' : ''}`}
            onClick={() => setStatusTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="zones-toolbar">
        <input
          className="zones-search"
          placeholder="Filter by username, fullname, company, or id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={tagFilter}
          onChange={e => setTagFilter(e.target.value)}
          className="zones-search"
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All tags</option>
          {allTbtvTags.map(t => <option key={t} value={t}>tag: {t}</option>)}
        </select>
      </div>

      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center',
          padding: '10px 14px', background: '#1e3a8a30', color: '#93c5fd',
          borderRadius: 6, marginBottom: 10, flexWrap: 'wrap',
        }}>
          <strong>{selected.size} selected</strong>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setShowBulkTags(true)}
            disabled={bulkRunning}
          >
            <Tag size={13} /> Apply tags
          </button>
          <span className="muted" style={{ fontSize: '0.75rem' }}>
            (Advertiser status changes must be done in AdSpeed's dashboard.)
          </span>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setSelected(new Set())}
            disabled={bulkRunning}
            style={{ marginLeft: 'auto' }}
          >
            <X size={13} /> Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="muted">Loading advertisers…</div>
      ) : sorted.length === 0 ? (
        <div className="muted">No advertisers found.</div>
      ) : (
        <table className="zones-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              {[
                { key: 'id',       label: 'ID' },
                { key: 'username', label: 'Username' },
                { key: 'fullname', label: 'Full name' },
                { key: 'company',  label: 'Company' },
                { key: 'status',   label: 'Status' },
              ].map(c => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  {c.label}
                  {sortBy === c.key && <span style={{ marginLeft: 4, color: '#f59e0b' }}>
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(a => {
              const id = String(advertiserField(a, 'id', 'AdvertiserId'))
              const isSel = selected.has(id)
              return (
                <tr
                  key={id}
                  onClick={() => navigate(`/sponsors/advertisers/${id}`)}
                  style={isSel ? { background: '#1e3a8a30', cursor: 'pointer' } : { cursor: 'pointer' }}
                >
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleSelect(id)} />
                  </td>
                  <td>{id}</td>
                  <td>{advertiserUsername(a)}</td>
                  <td>{advertiserFullname(a) || <span className="muted" style={{ fontSize: '0.78rem' }}>—</span>}</td>
                  <td>{advertiserCompany(a) || <span className="muted" style={{ fontSize: '0.78rem' }}>—</span>}</td>
                  <td>{advertiserField(a, 'status', 'Status')}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showCreate && (
        <AdvertiserEditor
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}

      {showBulkTags && (
        <BulkTagsModal
          count={selected.size}
          entityLabel="advertiser"
          onClose={() => setShowBulkTags(false)}
          onSubmit={handleBulkTags}
        />
      )}

      {createdPassword && (
        <PasswordRevealModal
          username={createdPassword.username}
          password={createdPassword.password}
          onClose={() => setCreatedPassword(null)}
        />
      )}
    </div>
  )
}

// Reveal-once modal shown after an auto-gen advertiser create succeeds.
// Browser doesn't remember the password after dismiss — admin must copy
// or note it now.
function PasswordRevealModal({ username, password, onClose }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#121212', border: '1px solid #f59e0b', borderRadius: 10,
          padding: 24, minWidth: 480, maxWidth: 560,
        }}
      >
        <h2 style={{ margin: 0, color: '#f59e0b', fontSize: '1.15rem' }}>
          Advertiser created — copy the password now
        </h2>
        <p className="muted" style={{ margin: '10px 0 16px', fontSize: '0.85rem' }}>
          AdSpeed stores this password securely. We don't keep a copy — once
          you close this dialog the password is gone. Share it with the
          advertiser through your usual secure channel.
        </p>
        <div style={{ marginBottom: 10 }}>
          <span className="muted" style={{ fontSize: '0.78rem' }}>Username</span>
          <div style={{ padding: '8px 10px', background: '#1a1a1a', borderRadius: 4, border: '1px solid #333', fontFamily: 'monospace' }}>
            {username}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <span className="muted" style={{ fontSize: '0.78rem' }}>Password</span>
          <div style={{ padding: '8px 10px', background: '#1a1a1a', borderRadius: 4, border: '1px solid #333', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {password}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={copy} className="sponsors-refresh-btn">
            {copied ? 'Copied!' : 'Copy password'}
          </button>
          <button onClick={onClose} className="btn btn-primary">
            I've saved it
          </button>
        </div>
      </div>
    </div>
  )
}
